import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { getPresetRateValues } from "shared/utils/tts-custom-editor";
import { SSMLTagsColors, SSMLTag } from "shared/constants";
import { presetRateValues } from "shared/models";
import { handleSSMLValueClick } from "shared/utils/tts-editor-helper";
import { getCurrentSSMLValue } from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import MenuWrapper from "../../components/MenuWrapper";
import CustomDropdown from "../../components/PresetCustomMenu/CustomDropdown";

interface RateMenuProps {
  editorRef: React.RefObject<EditorRef | null>;
  onRateChange?: (value: string) => void;
  onApply?: (value: string) => void;
}

export default function RateMenu({
  editorRef,
  onRateChange,
  onApply,
}: RateMenuProps) {
  const { t } = useTranslation();
  const [pendingRateValue, setPendingRateValue] = useState<string | null>(null);
  const [initialValue, setInitialValue] = useState<string>("default (100%)");
  const [activeRateValue, setActiveRateValue] = useState<string>("");
  const [customValue, setCustomValue] = useState<string>("");

  useEffect(() => {
    if (editorRef.current?.editor) {
      const currentRateValue = getCurrentSSMLValue(editorRef, SSMLTag.Rate);

      if (currentRateValue) {
        const presetMatch = presetRateValues.find(
          (preset) =>
            preset.value === currentRateValue ||
            preset.label === currentRateValue
        );

        if (presetMatch) {
          const displayText = `${presetMatch.label} (${presetMatch.value})`;
          setInitialValue(displayText);
          setPendingRateValue(presetMatch.value);
          setActiveRateValue(presetMatch.value);
          setCustomValue("");
        } else {
          if (currentRateValue.endsWith("%")) {
            const numericValue = currentRateValue.replace("%", "");
            setInitialValue(`Custom (${numericValue}%)`);
            setPendingRateValue(numericValue);
            setActiveRateValue(currentRateValue);
            setCustomValue(numericValue);
          } else {
            setInitialValue(`Custom (${currentRateValue})`);
            setPendingRateValue(currentRateValue);
            setActiveRateValue(currentRateValue);
            setCustomValue(currentRateValue);
          }
        }
        if (onRateChange) {
          onRateChange(currentRateValue);
        }
      } else {
        setInitialValue("default (100%)");
        setPendingRateValue(null);
        setActiveRateValue("100%");
        setCustomValue("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRef]);

  const handleRateSelect = (value: string) => {
    if (!value.trim() || parseFloat(value) <= 0) return;
    setPendingRateValue(value);

    const presetRateContent = getPresetRateValues(value);
    const rateValue = presetRateContent?.label || `${value}%`;

    // Update activeRateValue when a value is selected
    if (presetRateContent) {
      setActiveRateValue(presetRateContent.value);
      setCustomValue("");
    } else {
      setActiveRateValue(`${value}%`);
      setCustomValue(value);
    }

    if (onRateChange) {
      onRateChange(rateValue);
    }
  };

  const applyRate = () => {
    if (!pendingRateValue || !editorRef.current) return;

    const presetRateContent = getPresetRateValues(pendingRateValue);
    const rateValue = presetRateContent?.label || `${pendingRateValue}%`;
    const tagColor = SSMLTagsColors.ProsodyTag;

    handleSSMLValueClick(editorRef, SSMLTag.Rate, rateValue, tagColor, [
      "duration",
    ]);

    if (onApply) {
      onApply(rateValue);
    }
  };
  const isCustomRateValueActive = Boolean(
    activeRateValue &&
      !presetRateValues.some((preset) => preset.value === activeRateValue)
  );

  return (
    <MenuWrapper
      id="rate-menu-wrapper"
      onApply={applyRate}
      showDeleteButton={false}
      editorRef={editorRef}
      tagType={SSMLTag.Rate}
    >
      <CustomDropdown
        key={initialValue}
        showCustomTextField
        showPresetMenuList
        presetHeader={t("tts.toolbarMenu.rateMenu.presetRateHeader")}
        presetValues={presetRateValues}
        customHeader={t("tts.toolbarMenu.rateMenu.customRateHeader")}
        customTextFieldPlaceholder={t(
          "tts.toolbarMenu.rateMenu.customRatePlaceholder"
        )}
        handleClick={handleRateSelect}
        allowMathSymbols={false}
        defaultValue={initialValue}
        value={customValue}
        activeValue={activeRateValue}
        isCustomValueActive={isCustomRateValueActive}
      />
    </MenuWrapper>
  );
}
